/**
 * Inventory History Hook
 * Custom hook for managing inventory activity history and logging
 * Aligns with backend inventory.history module
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';

export const useInventoryHistory = (options = {}) => {
  const { autoFetch = true, limit = 50 } = options;
  
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: limit,
    total: 0,
    totalPages: 0
  });

  // Fetch activity history
  const fetchHistory = useCallback(async (page = 1, searchFilters = {}) => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Ensure authorization header is set
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const params = {
        page,
        limit: pagination.limit,
        ...searchFilters
      };
      
      // Using backend modular route structure
      const response = await axios.get('/api/inventory/activity-log', { params });
      
      // Backend returns array directly, not wrapped in object
      const activities = response.data || [];
      setHistory(activities);
      setPagination(prev => ({
        ...prev,
        page: page,
        total: activities.length,
        totalPages: Math.ceil(activities.length / pagination.limit)
      }));
    } catch (err) {
      console.error('Error fetching inventory history:', err);
      setError(err.message);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [token, pagination.limit]);

  // Log inventory action
  const logAction = useCallback(async (actionData) => {
    try {
      const response = await axios.post('/api/inventory/activity-log', actionData);
      // Refresh history to include new log entry
      await fetchHistory(pagination.page);
      return response.data;
    } catch (err) {
      console.error('Error logging inventory action:', err);
      // Don't show toast for logging errors as they're background operations
      throw err;
    }
  }, [fetchHistory, pagination.page]);

  // Filter history by action type
  const filterByAction = useCallback((actionType) => {
    return history.filter(entry => entry.action === actionType);
  }, [history]);

  // Filter history by item
  const filterByItem = useCallback((itemId) => {
    return history.filter(entry => entry.itemId === itemId);
  }, [history]);

  // Filter history by date range
  const filterByDateRange = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return history.filter(entry => {
      const entryDate = new Date(entry.createdAt);
      return entryDate >= start && entryDate <= end;
    });
  }, [history]);

  // Get recent activity (last 24 hours)
  const getRecentActivity = useCallback(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    return history.filter(entry => {
      const entryDate = new Date(entry.createdAt);
      return entryDate >= yesterday;
    });
  }, [history]);

  // Get activity summary
  const getActivitySummary = useCallback(() => {
    const summary = {
      totalActions: history.length,
      actionTypes: {},
      itemsAffected: new Set(),
      recentActivity: getRecentActivity().length
    };
    
    history.forEach(entry => {
      // Count action types
      summary.actionTypes[entry.action] = (summary.actionTypes[entry.action] || 0) + 1;
      
      // Count unique items
      if (entry.itemId) {
        summary.itemsAffected.add(entry.itemId);
      }
    });
    
    summary.itemsAffected = summary.itemsAffected.size;
    
    return summary;
  }, [history, getRecentActivity]);

  // Load next page
  const loadNextPage = useCallback(async () => {
    if (pagination.page < pagination.totalPages) {
      await fetchHistory(pagination.page + 1);
    }
  }, [fetchHistory, pagination.page, pagination.totalPages]);

  // Load previous page
  const loadPreviousPage = useCallback(async () => {
    if (pagination.page > 1) {
      await fetchHistory(pagination.page - 1);
    }
  }, [fetchHistory, pagination.page]);

  // Search history
  const searchHistory = useCallback(async (filters) => {
    await fetchHistory(1, filters);
  }, [fetchHistory]);

  // Auto-fetch on mount if enabled
  useEffect(() => {
    if (autoFetch && token) {
      fetchHistory();
    }
  }, [autoFetch, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    history,
    loading,
    error,
    pagination,
    fetchHistory,
    logAction,
    filterByAction,
    filterByItem,
    filterByDateRange,
    getRecentActivity,
    getActivitySummary,
    loadNextPage,
    loadPreviousPage,
    searchHistory,
    // Computed values
    totalEntries: history.length,
    hasNextPage: pagination.page < pagination.totalPages,
    hasPreviousPage: pagination.page > 1,
    activitySummary: getActivitySummary()
  };
};